import css from './button.module.scss';

import cx from 'classnames';
import _ from 'lodash';
import { memo } from 'react';
import { Link } from 'react-router-dom';

import KeyBind from '../KeyBind';
import { TStrKeyBind } from '../KeyBind/keyUtils';
import { useMessageModal } from '../MessageModal';

const extractDataProps = (props: TAnyObject): TAnyObject => {
  const keys = Object.keys(props).filter(key => key.startsWith('data-'));
  return _.pick(props, keys);
};

export type TButtonAction =
  | {
      onClick?: undefined;
      link: string;
    }
  | {
      link?: undefined;
      onClick: (evt: TAnyEvent) => Promise<void> | void;
    };

type TProps = TButtonAction & {
  children: TIconComponent | React.ReactNode;
  disabled?: boolean;
  className?: string;
  isLoading?: boolean;
  theme?: 'default' | 'primary' | 'flat' | 'menuItem';
  type?: 'submit' | 'button';
  kkb?: TStrKeyBind | TStrKeyBind[];
  kkbCheckInControls?: boolean;
  title?: string;
  onRef?: (domNode: HTMLElement | null) => void;
};
const Button: React.FC<TProps> = ({
  onClick,
  link,
  children,
  disabled,
  isLoading = false,
  className = '',
  type = 'button',
  title,
  theme = type === 'submit' ? 'primary' : 'default',
  kkb,
  kkbCheckInControls,
  onRef,
  ...props
}) => {
  const { showError } = useMessageModal();
  const Icon = typeof children === 'function' ? children : null;
  const content = Icon ? (
    <Icon className={css.icon} />
  ) : (
    (children as React.ReactNode)
  );

  const tagProps = {
    className: cx(css.button, Icon && css.iconButton, className),
    disabled: disabled || isLoading,
    ['data-theme']: theme,
    title,
    ref: onRef,
    ...extractDataProps(props as TAnyObject),
  };

  if (link) {
    return tagProps.disabled ? (
      <span {...tagProps}>{content}</span>
    ) : (
      <Link {...tagProps} to={link}>
        {content}
      </Link>
    );
  }

  return (
    <button
      data-loading={isLoading ? true : undefined}
      onClick={(evt: React.SyntheticEvent) => {
        orDie(onClick)(evt)?.catch(showError);
      }}
      {...{ type }}
      {...tagProps}
    >
      {kkb && onClick && (
        <KeyBind
          kkb={kkb}
          checkInControls={kkbCheckInControls}
          onKeyDown={onClick}
          disabled={tagProps.disabled}
        />
      )}
      {content}
    </button>
  );
};

export default memo(Button);
